import React, { useEffect, useRef, useState, memo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import eureka from 'eureka';
import eurekaMgrs from '@eureka/ui-managers';
import { MessageStrip, MessageStripDesign } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';

import { RootState } from 'src/common/redux';
import { MicroFrontendProps } from 'src/types';
import { DefaultLayout, MashupLayout } from '../components/Layout';

type Props = MicroFrontendProps & {
  getManifest?: () => Promise<{ data: unknown }>;
  getAppPreference?: () => Promise<{ data: unknown }>;
};
type RenderMicroFrontend = MicroFrontendProps & {
  container: ContainerRef;
  fetchManifestError: boolean;
  userPreferencePromise: Promise<{
    data: unknown;
  }>;
};
type RenderApp = MicroFrontendProps & {
  container: ContainerRef;
};
type ContainerRef = React.MutableRefObject<HTMLDivElement | null>;

const { UserPreferenceManager, eventBus } = eurekaMgrs;
const { ProxyHelper } = eurekaMgrs.ProxyManager;
const { fetchManifest, fetchApplicationManifestAssets, renderApplication, unmountApplication } =
  ProxyHelper;
const { getCurrentLanguage } = eureka.I18nProvider;
const { Spinner } = eureka.components;

export const hasLoggedin = () => {
  return !!window.hasLoggedin;
};

export const defaultGetAppPreference = (name: string) => {
  return Promise.resolve({
    data: {},
  });
};

export const renderApp = ({
  history,
  match,
  host,
  config,
  name,
  user,
  settings,
  container,
}: RenderApp) => {
  renderApplication(
    name,
    history,
    {
      history,
      match,
      host,
      user,
      eventBus,
      config,
      settings,
    },
    container,
    config,
  );
  // TODO:
  // eventBus.emit('i18n-update', null, getCurrentLanguage());
};

export const renderMicroFrontend = ({
  history,
  match,
  host,
  config,
  name,
  user,
  settings,
  container,
  fetchManifestError,
  userPreferencePromise,
}: RenderMicroFrontend) => {
  if (fetchManifestError) {
    return;
  }
  userPreferencePromise
    .then((res) => {
      UserPreferenceManager.setAppSetting(name, res.data);
    })
    .catch((err) => {
      /* istanbul ignore next */
      console.log('Load user preference error:', err);
    })
    .finally(() => {
      renderApp({ history, match, host, config, name, user, settings, container });
    });
};

export const MicroFrontendError = memo(() => {
  return (
    <MessageStrip
      style={{ margin: '10px' }}
      design={MessageStripDesign.Negative}
      hideIcon={false}
      hideCloseButton={false}
    >
      Failed to load asset manifest, please try again.
    </MessageStrip>
  );
});

const MicroFrontendInner = memo(
  ({ name, containerRef }: { name: string; containerRef: ContainerRef }) => {
    return (
      <div
        id="microfrontend-viewport"
        data-testid={`${name}-microfrontend-render-container`}
        style={{ height: '100%', background: ThemingParameters.sapBackgroundColor }}
      >
        <div id={`${name.toLowerCase()}-container`} style={{ height: '100%' }}>
          <div
            id={`${name.toLowerCase()}-content`}
            className="microfrontent-content"
            ref={containerRef}
            style={{ height: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              <Spinner />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

/* istanbul ignore next */
const MicroFrontend: React.FC<Props> = ({
  history,
  match,
  host,
  name,
  config,
  settings,
  user,
  getManifest,
  getAppPreference = defaultGetAppPreference,
}) => {
  const isDxp = useSelector((state: RootState) => state.common.isDxp);
  const location = useLocation();
  const containerRef = useRef(null);
  const [fetchManifestError, setFetchManifestError] = useState(false);
  const userPreferencePromise = (getAppPreference || defaultGetAppPreference)(name);
  // TODO:
  // const query = new URLSearchParams(location.search);
  // const isEmbed = query.get('embedded') === 'true';
  const isEmbed = isDxp;

  useEffect(() => {
    const containerCurrent = containerRef.current;
    if (name !== 'mfe-login' && !hasLoggedin()) {
      history.push('/login?application=dxp');
    } else if (containerCurrent) {
      fetchManifest({ host, name, getManifest }).then((manifest) => {
        // manifest host is equal to `http://localhost:2120`
        fetchApplicationManifestAssets(
          manifest,
          manifest?.files['main.js']?.startsWith('http') ? '' : host,
          name,
          config,
        ).then(
          () => {
            renderMicroFrontend({
              history,
              match,
              host,
              config,
              name,
              user,
              settings,
              container: containerCurrent,
              fetchManifestError,
              userPreferencePromise,
            });
          },
          (err) => {
            console.log('Load main asset error:', err);
            setFetchManifestError(true);
          },
        );
      });
    }
    return () => {
      if (fetchManifestError) {
        return;
      }
      unmountApplication(name);
    };
  }, []);

  if (fetchManifestError) {
    return (
      <DefaultLayout
        match={match}
        history={history}
        config={config}
        user={user}
        settings={settings}
      >
        <MicroFrontendError />
      </DefaultLayout>
    );
  }

  if (name.toLowerCase() === 'mfe-login') {
    return (
      <div style={{ height: '100%' }}>
        <MicroFrontendInner name={name} containerRef={containerRef} />
      </div>
    );
  }

  if (isEmbed) {
    return (
      <MashupLayout match={match} history={history} config={config} user={user} settings={settings}>
        <MicroFrontendInner name={name} containerRef={containerRef} />
      </MashupLayout>
    );
  }

  return (
    <DefaultLayout match={match} history={history} config={config} user={user} settings={settings}>
      <MicroFrontendInner name={name} containerRef={containerRef} />
    </DefaultLayout>
  );
};

export default MicroFrontend;
